import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, MenuItem, Select, FormControl, InputLabel,
  Tooltip, Avatar, CircularProgress, Alert, Grid
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon,
  Edit as EditIcon, Delete as DeleteIcon,
  Phone as PhoneIcon, Email as EmailIcon,
  WhatsApp as WhatsAppIcon, Instagram as InstagramIcon,
  YouTube as YouTubeIcon, Business as BusinessIcon,
  Person as PersonIcon, Close as CloseIcon,
  Upload as UploadIcon, Download as DownloadIcon
} from '@mui/icons-material';
import api from '../services/api';

const CONTACT_TYPES = [
  'Client', 'Talent', 'Vendor', 'Freelancer', 'Service Provider',
  'Government/Legal', 'Media/Press', 'Distributor/Platform', 'Referral',
  'Brand', 'Ad Agency', 'Other'
];
const STATUSES = ['Active', 'Inactive', 'Blacklisted'];

const TYPE_COLORS = {
  'Client':               'primary',
  'Talent':               'secondary',
  'Vendor':               'warning',
  'Freelancer':           'info',
  'Service Provider':     'success',
  'Government/Legal':     'error',
  'Media/Press':          'default',
  'Distributor/Platform': 'primary',
  'Referral':             'success',
  'Brand':                'secondary',
  'Ad Agency':            'warning',
  'Other':                'default',
};

const STATUS_COLORS = { Active: 'success', Inactive: 'default', Blacklisted: 'error' };

const EMPTY_FORM = {
  fullName: '', company: '', designation: '', contactType: 'Client',
  phone: '', mobile: '', whatsapp: '', email: '', city: '',
  instagram: '', youtube: '', tags: '', status: 'Active', notes: ''
};

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
}

export default function ContactDiary() {
  const [contacts, setContacts]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [search, setSearch]           = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [importing, setImporting]     = useState(false);
  const [importResult, setImportResult] = useState(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search)       params.search = search;
      if (filterType)   params.type   = filterType;
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/contacts', { params });
      setContacts(res.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, filterType, filterStatus]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditTarget(c);
    setForm({
      fullName: c.fullName || '', company: c.company || '', designation: c.designation || '',
      contactType: c.contactType || 'Client', phone: c.phone || '', mobile: c.mobile || '',
      whatsapp: c.whatsapp || '', email: c.email || '', city: c.city || '',
      instagram: c.instagram || '', youtube: c.youtube || '',
      tags: c.tags || '', status: c.status || 'Active', notes: c.notes || ''
    });
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); setDuplicateWarning(''); };

  const handleSave = async (force = false) => {
    if (!form.fullName.trim()) return;
    setSaving(true);
    setDuplicateWarning('');
    try {
      const payload = force ? { ...form, _forceSave: true } : form;
      if (editTarget) await api.put(`/contacts/${editTarget.id}`, payload);
      else            await api.post('/contacts', payload);
      closeDialog();
      fetchContacts();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.duplicate) {
        setDuplicateWarning(e.response.data.message);
      } else {
        setError(e.response?.data?.message || 'Failed to save contact');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      setDeleteConfirm(null);
      fetchContacts();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to delete');
    }
  };

  const f = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/contacts/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'contacts_template.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { setError('Failed to download template'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/contacts/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      fetchContacts();
    } catch (err) {
      setImportResult({ success: false, message: err.response?.data?.message || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '2.125rem' } }}>
          📒 Contact Diary
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Tooltip title="Download Excel template">
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
              Template
            </Button>
          </Tooltip>
          <Tooltip title="Import contacts from Excel">
            <Button variant="outlined" size="small" component="label"
              startIcon={importing ? <CircularProgress size={14} /> : <UploadIcon />} disabled={importing}>
              Import
              <input type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
            </Button>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd}>
            Add
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {importResult && (
        <Alert severity={importResult.success ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setImportResult(null)}>
          {importResult.message}
          {importResult.errors?.length > 0 && (
            <Box mt={0.5}>
              {importResult.errors.map((e, i) => (
                <Typography key={i} variant="caption" display="block">Row {e.row} — {e.name}: {e.reason}</Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {/* Filters — stack on mobile */}
      <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small" placeholder="Search name, company, tags…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 180px', minWidth: 0 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ flex: '1 1 120px', minWidth: 0 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">All Types</MenuItem>
            {CONTACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: '1 1 100px', minWidth: 0 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Company / Role</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Type</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Contact</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Tags</strong></TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : contacts.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No contacts found</TableCell></TableRow>
            ) : contacts.map(c => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ maxWidth: { xs: 130, sm: 'none' } }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: '0.7rem', bgcolor: 'primary.main', flexShrink: 0 }}>
                      {initials(c.fullName)}
                    </Avatar>
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{c.fullName}</Typography>
                      {/* Show type + status inline on mobile */}
                      <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                        <Chip label={c.contactType} size="small" color={TYPE_COLORS[c.contactType] || 'default'} sx={{ height: 18, fontSize: '0.62rem' }} />
                        <Chip label={c.status} size="small" color={STATUS_COLORS[c.status] || 'default'} sx={{ height: 18, fontSize: '0.62rem' }} />
                      </Box>
                      {c.city && <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{c.city}</Typography>}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2">{c.company || '—'}</Typography>
                  {c.designation && <Typography variant="caption" color="text.secondary">{c.designation}</Typography>}
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip label={c.contactType} size="small" color={TYPE_COLORS[c.contactType] || 'default'} />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Box display="flex" flexDirection="column" gap={0.3}>
                    {c.mobile    && <Box display="flex" alignItems="center" gap={0.5}><PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} /><Typography variant="caption">{c.mobile}</Typography></Box>}
                    {c.email     && <Box display="flex" alignItems="center" gap={0.5}><EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} /><Typography variant="caption">{c.email}</Typography></Box>}
                    {c.whatsapp  && <Box display="flex" alignItems="center" gap={0.5}><WhatsAppIcon sx={{ fontSize: 13, color: 'success.main' }} /><Typography variant="caption">{c.whatsapp}</Typography></Box>}
                    {c.instagram && <Box display="flex" alignItems="center" gap={0.5}><InstagramIcon sx={{ fontSize: 13, color: '#E1306C' }} /><Typography variant="caption">{c.instagram}</Typography></Box>}
                    {c.youtube   && <Box display="flex" alignItems="center" gap={0.5}><YouTubeIcon sx={{ fontSize: 13, color: 'error.main' }} /><Typography variant="caption">{c.youtube}</Typography></Box>}
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {(c.tags || '').split(',').filter(Boolean).map(tag => (
                      <Chip key={tag} label={tag.trim()} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Chip label={c.status} size="small" color={STATUS_COLORS[c.status] || 'default'} />
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm(c)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog — fullScreen on mobile */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={false}
        PaperProps={{ sx: { m: { xs: 1, sm: 2 }, width: '100%', maxHeight: { xs: '95vh', sm: '90vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          {editTarget ? 'Edit Contact' : 'Add Contact'}
          <IconButton size="small" onClick={closeDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {duplicateWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              ⚠️ {duplicateWarning}<br />
              <strong>You can save anyway if this is a different contact.</strong>
            </Alert>
          )}
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Full Name *" value={form.fullName} onChange={f('fullName')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type *</InputLabel>
                <Select value={form.contactType} label="Type *" onChange={f('contactType')}>
                  {CONTACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Company / Organization" value={form.company} onChange={f('company')}
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Designation / Role" value={form.designation} onChange={f('designation')}
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Mobile" value={form.mobile} onChange={f('mobile')}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone (Landline)" value={form.phone} onChange={f('phone')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="WhatsApp" value={form.whatsapp} onChange={f('whatsapp')}
                InputProps={{ startAdornment: <InputAdornment position="start"><WhatsAppIcon fontSize="small" sx={{ color: 'success.main' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Email" value={form.email} onChange={f('email')}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="City" value={form.city} onChange={f('city')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={f('status')}>
                  {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Instagram Handle" value={form.instagram} onChange={f('instagram')}
                InputProps={{ startAdornment: <InputAdornment position="start"><InstagramIcon fontSize="small" sx={{ color: '#E1306C' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="YouTube Handle" value={form.youtube} onChange={f('youtube')}
                InputProps={{ startAdornment: <InputAdornment position="start"><YouTubeIcon fontSize="small" sx={{ color: 'error.main' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Tags (comma separated)" value={form.tags} onChange={f('tags')}
                helperText="e.g. wedding, corporate, action" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" value={form.notes} onChange={f('notes')} multiline rows={3} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          {duplicateWarning && (
            <Button variant="outlined" color="warning" onClick={() => handleSave(true)} disabled={saving}>
              Save Anyway
            </Button>
          )}
          <Button variant="contained" onClick={() => handleSave(false)} disabled={saving || !form.fullName.trim()}>
            {saving ? <CircularProgress size={18} /> : (editTarget ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { m: { xs: 1, sm: 2 } } }}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteConfirm?.fullName}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
